import { API_URLS } from './urls';
import { getToken } from './auth';

// ─── Shared helper ────────────────────────────────────────────────────────────
// Kept local to the exams module (matches the pattern in attendance.ts).

async function apiFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      (data as { detail?: string; message?: string }).detail ??
      (data as { detail?: string; message?: string }).message ??
      `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return data as T;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type ExamType = 'mandatory' | 'optional';

/**
 * Shape returned by GET_ONGOING_EXAM_LIST.
 * One row per ongoing exam the teacher can enter marks for.
 */
export interface OngoingExamListItem {
  id: number;
  name: string;
  exam_type: ExamType;
  start_date: string;   // YYYY-MM-DD
  end_date: string;     // YYYY-MM-DD
  subject_count: number;
}

/**
 * Shape returned by GET_SUBJECTS_LIST.
 * One row per (class, section, subject) assignment the teacher can enter
 * marks for under a specific exam. `max_marks` / `min_marks` are the
 * overall subject totals (not per-component).
 */
export interface ExamSubjectListItem {
  class_id: number;
  class_name: string;
  section_id: number;
  section_name: string;
  subject_id: number;
  subject_name: string;
  student_count: number;
  max_marks: number;
  min_marks: number;
}

/** Per-student status flag in the exam students response. */
export type ExamStudentStatus = 'entered' | 'not_entered' | 'partial';

/** One student's row in the exam students response. Component values are
 *  `null` until the teacher has entered marks for that component. */
export interface ExamStudentMarkRow {
  student_id: number;
  roll_number: string;
  student_name: string;
  student_uid: string;
  theory: number | null;
  practical: number | null;
  oral?: number | null;
  status: ExamStudentStatus;
}

/**
 * Shape returned by GET_STUDENT_LIST. Contains the subject-level metadata
 * (including which components are assessed) alongside the roster.
 */
export interface ExamStudentListResponse {
  exam_id: number;
  class_id: number;
  section_id: number;
  subject_id: number;
  subject_name: string;
  max_marks: number;
  min_marks: number;
  has_theory: boolean;
  has_practical: boolean;
  has_oral: boolean;
  theory_max_marks: number;
  practical_max_marks: number;
  oral_max_marks: number;
  total: number;
  entered: number;
  pending: number;
  students: ExamStudentMarkRow[];
}

// ─── Endpoints ────────────────────────────────────────────────────────────────

/** Fetch the list of ongoing exams the current teacher can enter marks for. */
export async function getOngoingExamList(): Promise<OngoingExamListItem[]> {
  return apiFetch<OngoingExamListItem[]>(API_URLS.GET_ONGOING_EXAM_LIST, {
    method: 'GET',
  });
}

/**
 * Fetch the list of subjects (per class + section) the current teacher can
 * enter marks for, under a specific exam.
 */
export async function getExamSubjectsList(examId: number): Promise<ExamSubjectListItem[]> {
  return apiFetch<ExamSubjectListItem[]>(API_URLS.GET_SUBJECTS_LIST(examId), {
    method: 'GET',
  });
}

/**
 * Fetch the student roster + component metadata for a specific
 * (exam, class, section, subject). Response includes any marks already
 * saved plus subject-level pass/fail thresholds and component flags.
 */
export async function getExamStudentList(
  examId: number,
  classId: number,
  sectionId: number,
  subjectId: number,
): Promise<ExamStudentListResponse> {
  return apiFetch<ExamStudentListResponse>(
    API_URLS.GET_STUDENT_LIST(examId, classId, sectionId, subjectId),
    { method: 'GET' },
  );
}

// ─── Add marks (POST) ─────────────────────────────────────────────────────────

/** Per-student marks entry in the ADD_MARKS request body. */
export interface AddMarksStudentEntry {
  student_id: number;
  /** Map of subject_id (stringified) -> per-component marks. Only include
   *  components the subject actually assesses; missing components are
   *  treated as absent by the server. */
  subject_marks: Record<string, {
    theory?: number;
    practical?: number;
    oral?: number;
  }>;
}

export interface AddMarksRequest {
  marks: AddMarksStudentEntry[];
}

/** Exam status returned after a marks submission. */
export type ExamAfterMarksStatus = 'ongoing' | 'marks_entered' | 'result_released';

export interface AddMarksResponse {
  updated_count: number;
  exam_status: ExamAfterMarksStatus;
}

/**
 * Submit marks for one or more students under a given exam.
 * `payload.marks[].subject_marks[subject_id]` groups per-component values
 * so the server can update theory / practical / oral atomically.
 */
export async function addExamMarks(
  examId: number,
  payload: AddMarksRequest,
): Promise<AddMarksResponse> {
  return apiFetch<AddMarksResponse>(API_URLS.ADD_MARKS(examId), {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
